/**
 * Preferences Store
 * 
 * Manages user preferences for theme, compact mode, and regional settings.
 * Persists to localStorage and applies changes to the document.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'dark' | 'light' | 'system';
export type DateFormat = 'mdy' | 'dmy' | 'ymd';
export type Timezone = 'utc' | 'est' | 'pst' | 'cet' | 'jst' | 'ist';

interface PreferencesState {
  // Appearance
  theme: Theme;
  compactMode: boolean;
  
  // Regional
  timezone: Timezone;
  dateFormat: DateFormat;
  
  // Computed
  resolvedTheme: 'dark' | 'light';
  
  // Actions
  setTheme: (theme: Theme) => void;
  setCompactMode: (enabled: boolean) => void;
  setTimezone: (timezone: Timezone) => void;
  setDateFormat: (format: DateFormat) => void;
  applyTheme: () => void;
  applyCompactMode: () => void;
  initializePreferences: () => void;
}

const getSystemTheme = (): 'dark' | 'light' => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'dark';
};

const applyThemeToDocument = (theme: 'dark' | 'light') => {
  const root = document.documentElement;
  
  if (theme === 'light') {
    root.classList.remove('dark');
    root.classList.add('light');
  } else {
    root.classList.remove('light');
    root.classList.add('dark');
  }
};

const applyCompactModeToDocument = (enabled: boolean) => {
  const root = document.documentElement;
  
  if (enabled) {
    root.classList.add('compact');
  } else {
    root.classList.remove('compact');
  }
};

export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set, get) => ({
      theme: 'dark',
      compactMode: false,
      timezone: 'utc',
      dateFormat: 'mdy',
      resolvedTheme: 'dark',

      setTheme: (theme: Theme) => {
        const resolvedTheme = theme === 'system' ? getSystemTheme() : theme;
        set({ theme, resolvedTheme });
        applyThemeToDocument(resolvedTheme);
      },

      setCompactMode: (enabled: boolean) => {
        set({ compactMode: enabled });
        applyCompactModeToDocument(enabled);
      },

      setTimezone: (timezone: Timezone) => {
        set({ timezone });
      },

      setDateFormat: (format: DateFormat) => {
        set({ dateFormat: format });
      },

      applyTheme: () => {
        const { theme } = get();
        const resolvedTheme = theme === 'system' ? getSystemTheme() : theme;
        set({ resolvedTheme });
        applyThemeToDocument(resolvedTheme);
      },

      applyCompactMode: () => {
        const { compactMode } = get();
        applyCompactModeToDocument(compactMode);
      },

      initializePreferences: () => {
        const state = get();
        
        // Apply theme
        const resolvedTheme = state.theme === 'system' ? getSystemTheme() : state.theme;
        set({ resolvedTheme });
        applyThemeToDocument(resolvedTheme);
        
        // Apply compact mode
        applyCompactModeToDocument(state.compactMode);
        
        // Listen for system theme changes
        if (typeof window !== 'undefined' && window.matchMedia) {
          const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
          const handleChange = () => {
            if (get().theme === 'system') {
              const newResolvedTheme = getSystemTheme();
              set({ resolvedTheme: newResolvedTheme });
              applyThemeToDocument(newResolvedTheme);
            }
          };
          mediaQuery.addEventListener('change', handleChange);
        }
      },
    }),
    {
      name: 'gpu-platform-preferences',
      partialize: (state) => ({
        theme: state.theme,
        compactMode: state.compactMode,
        timezone: state.timezone,
        dateFormat: state.dateFormat,
      }),
    },
  ),
);

// Timezone display names
export const timezoneLabels: Record<Timezone, string> = {
  utc: 'UTC (Coordinated Universal Time)',
  est: 'Eastern Time (EST/EDT)',
  pst: 'Pacific Time (PST/PDT)',
  cet: 'Central European Time (CET/CEST)',
  jst: 'Japan Standard Time (JST)',
  ist: 'India Standard Time (IST)',
};

// Date format display examples
export const dateFormatExamples: Record<DateFormat, string> = {
  mdy: 'MM/DD/YYYY (01/13/2026)',
  dmy: 'DD/MM/YYYY (13/01/2026)',
  ymd: 'YYYY-MM-DD (2026-01-13)',
};
